// packages/engine/src/process-step-build.ts
//
// The leaf-step + modifier + reentry machinery shared by the render walker
// (process-walker.ts) and the trace producer (process-trace-producer.ts). Both
// consume the same emitter event stream and must build byte-identical
// WalkSteps / StepModifiers, so this logic lives in ONE place — the producer's
// per-outcome traces are then leaf-for-leaf equal to the render walker's under
// the equivalent selection (the sync-equality anchor, design §cross-cutting).
//
// Pure except for the two documented side-effect accumulators (`asByName`,
// `reentries`) buildLeafStep / resolveReentries write through — the same
// accumulators the walker threaded as run-local closures.

import type { QualifiedName } from "./types.js";
import type {
  CallNode,
  NoteNode,
  ProseNode,
  AwaitNode,
  ReentryNode,
  ReversibleNode,
  SpliceNode,
} from "./process-model.js";
import { unwindActionText } from "./process-print.js";
import type {
  NodeId,
  Reentry,
  StepModifiers,
  WalkStep,
} from "./process-walker-types.js";
import type { EmittedStep, EmitContainer, EmitGroup } from "./process-step-emitter.js";

/** Top-level modifiers — every step starts here and layers container deltas. */
export const DEFAULT_MODS: StepModifiers = {
  inEach: false,
  inParallel: false,
  inCatch: false,
  subprocessChain: [],
};

/** Enter a `dist` (choreographed) region — set once, never cleared. */
export function applyDist(m: StepModifiers, node: unknown): StepModifiers {
  return node != null &&
    typeof node === "object" &&
    "owner" in node &&
    (node as { owner?: { form?: string } }).owner?.form === "dist" &&
    !m.dist
    ? { ...m, dist: true }
    : m;
}

/** The StepModifiers a group's children inherit — one container/group delta
 *  layered on the parent's modifiers. Transparent containers return the parent
 *  unchanged. */
export function groupDelta(parent: StepModifiers, c: EmitContainer, g: EmitGroup): StepModifiers {
  const parentSel = parent.branchSelected ?? true;
  switch (c.irKind) {
    case "loop":
      return { ...parent, inEach: true };
    case "retry":
      if (g.role === "attempt") return { ...parent, inEach: true, retry: true };
      if (g.role === "catch") return { ...parent, inEach: true, retry: true, inCatch: true };
      return { ...parent, inEach: true }; // elseBody
    case "branch":
      return { ...parent, branchLabel: g.label, branchSelected: parentSel && g.selected };
    case "select":
      return { ...parent, branchLabel: g.label, branchSelected: parentSel };
    case "guard":
      return g.role === "attempt"
        ? { ...parent, branchLabel: "try", branchSelected: parentSel && g.selected }
        : { ...parent, branchLabel: g.label, inCatch: true, branchSelected: parentSel && g.selected };
    case "parallel":
      return { ...parent, inParallel: true, parallelGroup: c.nodeId };
    case "reversible":
      if (g.role === "unwind") return { ...parent, inCatch: true, compensation: true, reversibleId: c.nodeId };
      if (g.role === "item") {
        // Mark the item's FORWARD leaf(s) as a reversible forward + carry its
        // paired unwind text — so the editor can offer an inline `unwind` editor
        // exactly here (a compensation step, by contrast, is `compensation`).
        const item = g.itemIndex != null ? (c.node as ReversibleNode).items[g.itemIndex] : undefined;
        const unwindText = item?.unwind ? unwindActionText(item.unwind.action, 0) : undefined;
        return { ...parent, reversibleForward: true, reversibleId: c.nodeId, ...(unwindText !== undefined ? { unwindText } : {}) };
      }
      return parent;
    case "splice":
      return {
        ...parent,
        subprocessChain: [...parent.subprocessChain, (c.node as SpliceNode).target.join(".")],
        collapsedSubprocessGroup: c.expanded ? undefined : c.nodeId,
      };
    case "owner_block":
    default:
      return parent; // transparent
  }
}

/**
 * Build the WalkStep for one index-consuming emitter unit (a call HOP, note,
 * prose row, await, or `go`). For a `go` it reads the in-walk `as` registry for
 * backward resolution and records a {@link Reentry} (forward-referenced `go`s
 * are fixed up by {@link resolveReentries} after the whole walk).
 */
export function buildLeafStep(
  u: EmittedStep,
  mods: StepModifiers,
  asByName: ReadonlyMap<string, WalkStep>,
  reentries: Reentry[],
): WalkStep {
  if (u.kind === "call") {
    const node = u.node as CallNode;
    const hop = u.hop!;
    const to = hop.callee.anonymous ? hop.callee.modulePath : hop.callee.interfacePath;
    return {
      kind: "step",
      nodeId: u.nodeId,
      stepIndex: u.stepIndex,
      from: hop.caller,
      to,
      // §1.5: hop 0's caller carries the node owner's instance alias; every
      // hop's callee carries its own. Kept for lane/edge labelling.
      ...(u.hopIndex === 0 && node.owner.instance ? { fromInstance: node.owner.instance } : {}),
      ...(hop.callee.instance ? { toInstance: hop.callee.instance } : {}),
      ...(hop.callee.anonymous ? { toAnonymous: true } : {}),
      direction: hop.direction,
      bind: u.isLastHop ? node.bind?.name : undefined,
      args: hop.args?.map((a) => a.text),
      ...(u.isLastHop && node.asName ? { asName: node.asName.name } : {}),
      ...(u.isLastHop && node.comment ? { comment: node.comment.text } : {}),
      modifiers: mods,
    };
  }
  if (u.kind === "note") {
    const node = u.node as NoteNode;
    const lane = node.owner.resolved ?? [];
    const latField = node.fields?.find((f) => f.path.length === 1 && f.path[0] === "latency");
    const noteLatency =
      latField?.value && (latField.value.kind === "identifier" || latField.value.kind === "string")
        ? latField.value.text
        : undefined;
    return { kind: "step", nodeId: u.nodeId, stepIndex: u.stepIndex, from: lane, to: lane, noteText: node.text.text, ...(noteLatency ? { noteLatency } : {}), modifiers: mods };
  }
  if (u.kind === "prose") {
    // v0.9.0 §2.6: ownerless positional prose — empty lane; the swimlane
    // inherits the previous row's lane, matching the positional semantics.
    const node = u.node as ProseNode;
    return { kind: "step", nodeId: u.nodeId, stepIndex: u.stepIndex, from: [], to: [], noteText: node.text.text, prose: true, modifiers: mods };
  }
  if (u.kind === "await") {
    const node = u.node as AwaitNode;
    const lane = node.owner.resolved ?? [];
    return { kind: "step", nodeId: u.nodeId, stepIndex: u.stepIndex, from: lane, to: lane, noteText: `await ${node.target.text}`, modifiers: mods };
  }
  // go
  const node = u.node as ReentryNode;
  const target = node.target.name;
  const targetStep = asByName.get(target);
  const lane = targetStep?.from ?? [];
  const ws: WalkStep = {
    kind: "step",
    nodeId: u.nodeId,
    stepIndex: u.stepIndex,
    from: lane,
    to: lane,
    noteText: `go ${target}`,
    reentryTarget: target,
    ...(node.comment ? { comment: node.comment.text } : {}),
    modifiers: mods,
  };
  reentries.push({ fromNodeId: u.nodeId, target, toNodeId: targetStep?.nodeId });
  return ws;
}

/**
 * Forward pass over the assembled steps: resolve `go`s whose `as` target
 * appears LATER (the in-walk backward resolution only saw already-walked
 * steps), first-wins on duplicate `as`, and fill the target lane onto a `go`
 * step that had none. Mutates `steps` / `reentries` in place — matching the
 * walker's post-emit fixup.
 */
export function resolveReentries(steps: readonly WalkStep[], reentries: readonly Reentry[]): void {
  const allAs = new Map<string, WalkStep>();
  for (const s of steps) if (s.asName && !allAs.has(s.asName)) allAs.set(s.asName, s);
  for (const re of reentries) {
    if (re.toNodeId) continue;
    const target = allAs.get(re.target);
    if (target) (re as { toNodeId?: NodeId }).toNodeId = target.nodeId;
  }
  for (const s of steps) {
    if (!s.reentryTarget || s.from.length > 0) continue;
    const target = allAs.get(s.reentryTarget);
    if (target && target.from.length > 0) {
      (s as { from: QualifiedName; to: QualifiedName }).from = target.from;
      (s as { to: QualifiedName }).to = target.from;
    }
  }
}
